import { clsx } from 'clsx';
import { Check, Minus } from 'lucide-react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

export type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>;

/**
 * Square checkbox on Radix (keyboard, `aria-checked`). `checked="indeterminate"` ("some rows
 * selected") draws a dash instead of a tick. Label it with `<CheckboxField>` or `<Label htmlFor>`.
 */
export function Checkbox({ className, ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root className={clsx('check', className)} {...props}>
      <CheckboxPrimitive.Indicator className="check__indicator">
        <Check className="check__tick" size={14} strokeWidth={3} aria-hidden="true" />
        <Minus className="check__dash" size={14} strokeWidth={3} aria-hidden="true" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export interface CheckboxFieldProps extends Omit<CheckboxProps, 'children'> {
  label: ReactNode;
  /** A number shown at the end of the row (facet counts). */
  count?: number | undefined;
}

/** A labelled checkbox row: the whole row is the click target (≥ 44 px tall). */
export function CheckboxField({
  label,
  count,
  id,
  className,
  disabled,
  ...props
}: CheckboxFieldProps) {
  return (
    <label className={clsx('check-field', className)} data-disabled={disabled || undefined}>
      <Checkbox id={id} disabled={disabled} {...props} />
      <span className="check-field__label">{label}</span>
      {count === undefined ? null : <span className="check-field__count">{count}</span>}
    </label>
  );
}
